import { useState, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { useAppStore } from "@/store";
import {
  useDocumentRepo,
  type Document,
  type DocumentSortBy,
  type DocumentNodeType,
  type DocumentSource,
} from "@/hooks/use-document-repo";
import {
  useDocumentFilters,
  loadDocumentFilters,
  defaultDocumentFilters,
  type DocumentFiltersState,
} from "@/hooks/use-document-filters";
import { type DocumentScope } from "@/components/documents/document-scope-picker";
import { useDeleteDocument } from "@/hooks/use-document-actions";
import { fetchAndSaveDocument } from "@/lib/document-download";
import { PageHeader, Pagination, EmptyState, SkeletonTable } from "@/components/ui";
import { SearchIcon } from "@/components/icons";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/use-debounce";
import { DocumentRepoTable } from "@/components/documents/document-repo-table";
import {
  DocumentRepoFilters,
  type DocumentRepoFilterValues,
} from "@/components/documents/document-repo-filters";
import { DocumentPreviewDrawer } from "@/components/documents/document-preview-drawer";
import { DocumentDeleteDialog } from "@/components/documents/document-delete-dialog";

/** A shareable view of the finder, serialized to the URL search params. */
interface DocumentsSearch {
  /** Picked entity scope: `<node_type>:<node_id>`. */
  scope?: string;
  /** Hero filename search. */
  q?: string;
  /** Comma-joined source filter. */
  source?: string;
  type?: string;
  after?: string;
  before?: string;
  /** Sort as `<sort_by>:<dir>`; omitted when the default (created_at:desc). */
  sort?: string;
}

const PAGE_SIZE = 25;

// Entity scope axes the omni-scope picker can pick (mirrors ENTITY_GROUPS in the
// picker). Other DocumentNodeType values are never a scope.
const ENTITY_NODE_TYPES: DocumentNodeType[] = [
  "facility",
  "device_installation",
  "account",
  "org_tester",
  "field_credential",
  "test_kit",
  "testing_company",
];
const ALL_SOURCES: DocumentSource[] = [
  "document",
  "form",
  "task",
  "notice",
  "cromerr",
  "report_form_output",
];
const SORT_KEYS: DocumentSortBy[] = ["created_at", "filename", "size_bytes", "source"];

function isEntityNodeType(value: string): value is DocumentNodeType {
  return (ENTITY_NODE_TYPES as string[]).includes(value);
}

function parseSources(raw: string | undefined): DocumentSource[] | undefined {
  if (!raw) return undefined;
  const parsed = raw
    .split(",")
    .filter((s): s is DocumentSource => (ALL_SOURCES as string[]).includes(s));
  return parsed.length > 0 ? parsed : undefined;
}

function parseSort(
  raw: string | undefined,
): { sortBy: DocumentSortBy; sortDir: "asc" | "desc" } | null {
  if (!raw) return null;
  const [by, dir] = raw.split(":");
  if (by && (SORT_KEYS as string[]).includes(by) && (dir === "asc" || dir === "desc")) {
    return { sortBy: by as DocumentSortBy, sortDir: dir };
  }
  return null;
}

/**
 * Build the mount-time state from the URL search params. Returns null when the
 * URL carries no finder state (the hook then falls back to localStorage, else
 * defaults). A URL-restored scope borrows its label from localStorage when the
 * node ids match, else falls back to a generic label (rolls_up is derived).
 */
function searchToOverride(search: DocumentsSearch, orgId: string): DocumentFiltersState | null {
  const hasContent =
    !!search.scope ||
    !!search.q ||
    !!search.source ||
    !!search.type ||
    !!search.after ||
    !!search.before ||
    !!search.sort;
  if (!hasContent) return null;

  const stored = loadDocumentFilters(orgId);
  let scope: DocumentScope | null = null;
  if (search.scope) {
    const [type, id] = search.scope.split(":");
    if (type && id && isEntityNodeType(type)) {
      const fromStore =
        stored?.scope && stored.scope.node_type === type && stored.scope.node_id === id
          ? stored.scope
          : null;
      scope = fromStore ?? {
        node_type: type,
        node_id: id,
        label: "",
        rolls_up: type === "facility",
      };
    }
  }

  const base = defaultDocumentFilters();
  const sort = parseSort(search.sort);
  return {
    scope,
    q: search.q || undefined,
    source: parseSources(search.source),
    type: search.type || undefined,
    created_after: search.after || undefined,
    created_before: search.before || undefined,
    sortBy: sort?.sortBy ?? base.sortBy,
    sortDir: sort?.sortDir ?? base.sortDir,
  };
}

/** Serialize the finder state to the URL search params (defaults omitted). */
function stateToSearch(s: DocumentFiltersState): DocumentsSearch {
  const isDefaultSort = s.sortBy === "created_at" && s.sortDir === "desc";
  return {
    scope: s.scope ? `${s.scope.node_type}:${s.scope.node_id}` : undefined,
    q: s.q || undefined,
    source: s.source && s.source.length > 0 ? s.source.join(",") : undefined,
    type: s.type || undefined,
    after: s.created_after || undefined,
    before: s.created_before || undefined,
    sort: isDefaultSort ? undefined : `${s.sortBy}:${s.sortDir}`,
  };
}

export const Route = createFileRoute("/_authenticated/documents/")({
  validateSearch: (search: Record<string, unknown>): DocumentsSearch => ({
    scope: typeof search.scope === "string" ? search.scope : undefined,
    q: typeof search.q === "string" ? search.q : undefined,
    source: typeof search.source === "string" ? search.source : undefined,
    type: typeof search.type === "string" ? search.type : undefined,
    after: typeof search.after === "string" ? search.after : undefined,
    before: typeof search.before === "string" ? search.before : undefined,
    sort: typeof search.sort === "string" ? search.sort : undefined,
  }),
  component: DocumentsRepositoryPage,
});

export function DocumentsRepositoryPage() {
  const { t } = useTranslation(["documents", "org", "common"]);
  const search = Route.useSearch();
  const navigate = useNavigate();
  const orgId = useAppStore((s) => s.activeOrgId);
  const toasts = useToast();
  const deleteMutation = useDeleteDocument();

  // URL wins on mount (captured once); the hook falls back to localStorage,
  // then defaults.
  const [initialOverride] = useState(() => searchToOverride(search, orgId ?? ""));
  const { state, patch, applyScopeChange, resetAll } = useDocumentFilters(
    orgId ?? "",
    initialOverride,
  );

  const [offset, setOffset] = useState(0);
  const [previewDoc, setPreviewDoc] = useState<Document | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<Document | null>(null);

  // Hero filename search — local immediate input, debounced into state.q.
  const [heroInput, setHeroInput] = useState(state.q ?? "");
  const debouncedHero = useDebounce(heroInput, 300);
  useEffect(() => {
    if (debouncedHero !== (state.q ?? "")) patch({ q: debouncedHero || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire only on debounced value change
  }, [debouncedHero]);
  // Keep the input in sync when state.q changes out-of-band (Reset all).
  useEffect(() => {
    setHeroInput(state.q ?? "");
  }, [state.q]);

  // Reset paging whenever the scope/filters/sort/search change.
  useEffect(() => {
    setOffset(0);
  }, [state]);

  // Mirror the state into the URL (replace) so a scoped/filtered view is
  // shareable. State changes never re-derive from the URL (the override is
  // captured once), so there is no feedback loop.
  useEffect(() => {
    void navigate({ to: "/documents", search: stateToSearch(state), replace: true });
  }, [state, navigate]);

  const sorting: SortingState = useMemo(
    () => [{ id: state.sortBy, desc: state.sortDir === "desc" }],
    [state.sortBy, state.sortDir],
  );
  const handleSortingChange = (next: SortingState) => {
    const first = next[0];
    if (first) {
      patch({ sortBy: first.id as DocumentSortBy, sortDir: first.desc ? "desc" : "asc" });
    } else {
      patch({ sortBy: "created_at", sortDir: "desc" });
    }
  };

  const filterValues: DocumentRepoFilterValues = useMemo(
    () => ({
      source: state.source,
      type: state.type,
      created_after: state.created_after,
      created_before: state.created_before,
    }),
    [state.source, state.type, state.created_after, state.created_before],
  );
  const handleFiltersChange = (next: DocumentRepoFilterValues) =>
    patch({
      source: next.source,
      type: next.type,
      created_after: next.created_after,
      created_before: next.created_before,
    });

  const listParams = useMemo(
    () => ({
      node_type: state.scope?.node_type,
      node_id: state.scope?.node_id,
      search: state.q,
      source: state.source,
      type: state.type,
      created_after: state.created_after,
      created_before: state.created_before,
      sort_by: state.sortBy,
      sort_dir: state.sortDir,
      limit: PAGE_SIZE,
      offset,
    }),
    [state, offset],
  );

  const { data, isLoading, isError, error } = useDocumentRepo(listParams);

  const handleDownload = (doc: Document) => {
    if (!orgId) return;
    void fetchAndSaveDocument(orgId, doc).catch(() => {
      toasts.error("documents:repo.toast.downloadFailed");
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteDoc) return;
    deleteMutation.mutate(deleteDoc, {
      onSuccess: () => {
        toasts.success("documents:repo.toast.deleted");
        if (previewDoc?.id === deleteDoc.id) setPreviewDoc(null);
        setDeleteDoc(null);
      },
      onError: () => toasts.error("documents:repo.toast.deleteFailed"),
    });
  };

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  const items = data?.items ?? [];
  const total = data?.total_count ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const cardTitle = state.scope
    ? state.scope.label || t("repo.selectedNode")
    : t("repo.allDocuments");
  const countText = t("repo.rollupCount", { count: total });
  const subtitle = state.scope
    ? state.scope.rolls_up
      ? `${countText} · ${cardTitle}, ${t("repo.subtitle.inclDevices")}`
      : `${countText} · ${cardTitle}`
    : countText;

  return (
    <div className="space-y-5 p-8">
      <PageHeader
        title={t("repo.title")}
        actions={
          <div className="relative w-full sm:w-96">
            <SearchIcon
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={heroInput}
              onChange={(e) => setHeroInput(e.target.value)}
              placeholder={t("repo.globalSearchPlaceholder")}
              aria-label={t("repo.globalSearchPlaceholder")}
              className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm text-on-surface shadow-sm placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>
        }
      />

      {/* Flush Drive-density card (mockup 05 `.card`): toolbar + chips, the
          roll-up header, the results table, then the footer pager. */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <DocumentRepoFilters
          scope={state.scope}
          onScopeChange={applyScopeChange}
          onClearScope={() => patch({ scope: null })}
          filters={filterValues}
          onChange={handleFiltersChange}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          onResetAll={() => {
            setHeroInput("");
            resetAll();
          }}
        />

        {/* Roll-up header (mockup `.mhd`): bold scope title + count subtitle. */}
        <div className="flex flex-wrap items-baseline gap-2.5 px-3.5 py-3">
          <h2 className="text-[15px] font-bold text-on-surface">{cardTitle}</h2>
          <span className="text-xs text-muted">{subtitle}</span>
        </div>

        {isError ? (
          <div className="m-3.5 rounded-md bg-danger/10 p-4 text-danger">
            {t("repo.loadFailed")}:{" "}
            {error instanceof Error ? error.message : t("unknownError", { ns: "common" })}
          </div>
        ) : isLoading ? (
          <div className="p-3.5">
            <SkeletonTable columns={7} rows={6} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title={t("repo.empty")}
              description={t("repo.emptyDescription")}
              icon={
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z"
                  />
                </svg>
              }
            />
          </div>
        ) : (
          <>
            <DocumentRepoTable
              docs={items}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              onPreview={setPreviewDoc}
              onDownload={handleDownload}
              onDelete={setDeleteDoc}
            />
            {/* Slim footer (mockup `.mfoot`) — row count + paging. */}
            <div className="border-t border-gray-100 px-3.5 py-2.5 dark:border-gray-700">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
              />
            </div>
          </>
        )}
      </div>

      <DocumentPreviewDrawer
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onDownload={handleDownload}
        onDelete={setDeleteDoc}
      />

      <DocumentDeleteDialog
        doc={deleteDoc}
        isPending={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDoc(null)}
      />
    </div>
  );
}
